import loadingState from '@/states/loadingState';
import { useCallback } from 'react';
import { useRecoilState } from 'recoil';

/**
 * loadingState設定用フック
 * @param url 実行するAPIのURL
 * @returns startLoading 開始
 * @returns finishLoading 終了
 */
const useSetLoading = () => {
  const [loading, setIsLoading] = useRecoilState(loadingState);

  const startLoading = useCallback((url: string) => {
    // URL単位で積み上げることで、複数APIが並列実行されても個別に解除できるようにする。
    setIsLoading((prev) => prev.concat([url]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finishLoading = useCallback((url: string) => {
    // 完了したAPIだけを配列から除外し、他の通信中状態は維持する。
    setIsLoading((prev) => prev.filter((str) => str !== url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * ローディング状態を取得する。
   * @param keys 特定のローディング状態を取得する場合に使用する
   * @returns ローディング状態が存在する場合は、trueを返却する。それ以外は、falseを返却する。
   * @deprecated 「useLoading.ts」を使用してください。
   * ```
   * 使用すると以下の警告情報がブラウザコンソールに出力されます。ローディング状態を取得する場合は「useLoading.ts」を利用してください。
   * Warning:
   * Cannot update a component (`Batcher`) while rendering a different component (`xxxx`).
   * To locate the bad setState() call inside `xxxx`,
   * follow the stack trace as described in https://reactjs.org/link/setstate-in-render Error Component Stack
   * ```
   */
  const isLoading = useCallback(
    (keys?: string | string[]): boolean => {
      // 最新の状態を取得する。
      let state: string[] = [];
      setIsLoading((prev) => {
        state = prev;
        return prev;
      });
      // 取得した情報で表示／非表示の判断処理を実施する。
      if (state.length === 0) {
        return false;
      }
      if (typeof keys === 'string') {
        // 単一APIのローディング有無だけを参照する。
        return state.includes(keys);
      }
      if (Array.isArray(keys)) {
        // 指定した複数APIのどれかが通信中ならローディング表示にする。
        return state.some((e) => keys.includes(e));
      }
      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading]
  );

  return { isLoading, startLoading, finishLoading };
};

export default useSetLoading;
